import { AppProvider } from './context/AppProvider'
import { FC, useEffect, useRef } from 'react'
import { DateSelectionPage } from './pages/DateSelectionPage'
import { TableLayoutPage } from './pages/TableLayoutPage'
import { applyVenueStyles, deleteItem, load, mapShifts, parseDate, priceRulesSort, processTableLayout, _, save } from './utils/helpers'
import { useApp } from './context/useApp'
import {
	translator,
	rumba,
	DEFAULT_CACHE_DATE,
	PRICE_RULES_CACHE,
	VENUE_DATA_CACHE,
	ROUTES,
	KEYS_USED,
	TABLE_MAP_ROTATIONS,
	metrics,
	cache,
	DEFAULT_DIALOG_HANDLE,
	intervals,
	CHECK_BOOKING_TABLES_INTERVAL,
} from './utils/symbols'
import './styles/app.css'
import './styles/theme.css'
import './styles/test.css'
import { PersonalDetailsPage } from './pages/PersonalDetailsPage'
import { Dialog } from './components/dialog/Dialog'
import { Id, PriceRule } from './types'
import { CalendarWidget } from './pages/CalendarWidget'
import { useTableLayoutRefresh } from './hooks/useTableLayoutRefresh'

const BookingEngine: FC = () => {

	const {
		state: { venueData, booking, priceRules, route, dialogHandles, forcedRenders },
		actions: {
			updateScreen,
			updatePriceRules,
			updateProducts,
			updateVenueData,
			updateShifts,
			forceRender,
			genericError,
			navigate,
		},
	} = useApp()
	
    const appLoadDone = useRef(false)
	const tableLayout = useTableLayoutRefresh()

	const handleVenueData = async () => {

		//venue data is being cached, change cache time in symbols.ts
		const cacheExpired = VENUE_DATA_CACHE < (Date.now() - Number(load('localStorage', 'venue-data-date', DEFAULT_CACHE_DATE)) )
		if (venueData && !cacheExpired) {
			applyVenueStyles(venueData)
			return
		}

		//cache expired so we request venue data
		const response = await rumba.getVenueData()
		if (response.result !== 'ok') {
			genericError(handleVenueData)
			return
		}

		save('localStorage','venue-data-date', Date.now())
		
		updateVenueData(response.data)
		applyVenueStyles(response.data)
	}

	const handlePriceRules = async () => {

		// after ensuring we have loaded the price rules, 
		// through cache or requesting them, call getShifts (if needed)

		//price rules are being cached, change cache time in symbols.ts
		let updatedPriceRulesReference = priceRules
		const cacheExpired = PRICE_RULES_CACHE < (Date.now() - Number(load('localStorage', 'price-rules-date', DEFAULT_CACHE_DATE)))

		if (cacheExpired) {
			//cache expired so we request priceRules
			const response = await rumba.getPriceRules()
			if (response.result !== 'ok') {
				genericError(handlePriceRules)
				return
			}
	
			updatedPriceRulesReference = response.priceRules.sort((a, b) => priceRulesSort(a, b, booking.journey))
			updatePriceRules(updatedPriceRulesReference)
		}

		getShifts(updatedPriceRulesReference)
	}

	const getProducts = async () => {

		if (booking.eventId === 0) return

		const productsInCache = cache.get('products',booking.eventId)
		if (productsInCache) {
			updateProducts(productsInCache)
			return
		}

		// products are requested when a event is selected, so only request them 
		// if when loading the page, a event is already selected.
		const response = await rumba.getProducts(booking.date, booking.shiftId, booking.eventId)
		if (response.result !== 'ok') {
			genericError(getProducts)
			return
		}

		updateProducts(response.data)
		cache.add('products', booking.eventId, response.data)
	}

	const getShifts = async (updatedPriceRules: PriceRule[]) => {

		if (booking.step === 'date' || booking.step === 'shift') return
		const date = parseDate('yyyy-mm-dd', booking.date)

		const shiftsInCache = cache.get('shifts',date)
		if (shiftsInCache) {
			updateShifts(shiftsInCache)
			return
		}

		// shifts will be requested in shift step, so only request them 
		// if we are ahead of shift step
		const response = await rumba.getShifts(date)
		if (response.result !== 'ok') {
			genericError(() => getShifts(updatedPriceRules))
			return
		}

		const newShifts = response.shifts.map(shift => mapShifts(
			shift, 
			updatedPriceRules,
			booking.getBookingDataByReference()
		))
		updateShifts(newShifts)
		cache.add('shifts', date, newShifts)
	}

	const getLayout = async () => {

		if ((booking.step !== 'personal-details' &&  booking.step !== 'payment') || !venueData) return
		await tableLayout.refresh()
	}

	useEffect(() => {

		//genericError is unavailable here, the dialogs are not ready to use.
		//do not add promises in this use effect nor any useEffect with no dependencies
		//if a useEffect calls a promise must have at least dialogHandles in its dependency array

		//check if venue has been changed
		//this must be the first app step, do not add anything above
		if (rumba.venueId !== booking.venueId) {
			deleteItem('booking-data')
			deleteItem('price-rules')
			deleteItem('price-rules-date')
			deleteItem('venue-data')
			deleteItem('venue-data-date')
			window.location.reload()
		}

		//handle journey
		document.body.setAttribute('data-journey', booking.journey)
		document.body.classList.add(`v${rumba.venueId}`)
		rumba.setJourney(booking.journey)

		// translations won't work on local as they ar added in prod
		translator.mergeTranslations((window as any).bookingTranslations)
		forceRender()

		//url params handling, display calendar widget & others
		const params = new URLSearchParams(window.location.search)
		const dateParam = params.get('date')
		const routeParam = params.get('route')
		//deep link to go directly to shift step
		if (dateParam) {
			booking.setBookingStep('shift')
			booking.selectDate(dateParam).render()
			navigate(ROUTES.TABLE_LAYOUT)
		}
		//deep link to display the calendar widget
		if (params.has('calendar')) {
			booking.setBookingStep('date')
			booking.unSelectDate().render()
			navigate(ROUTES.CALENDAR)
		}
		//deep link to render a pseudo rout
		if (routeParam) navigate(routeParam)
		
		// opening / closing inspect element does not trigger resize event listener
		// so some styles may seem broken, just reload. 
		window.addEventListener('resize', updateScreen)
		return () => window.removeEventListener('resize', updateScreen)

	}, [])

	useEffect(() => {

		if (dialogHandles.size === 0 || appLoadDone.current) return

		handleVenueData()
		getProducts()
		handlePriceRules()
		getLayout()

		metrics.loadEvent(booking.getBookingDataByReference())

		//avoid requesting twice
        appLoadDone.current = true

	}, [dialogHandles])

	useEffect(() => {
		document.body.setAttribute('data-page', route)
	}, [route])

	useEffect(() => {
		document.body.setAttribute('data-step', booking.step)
	}, [booking.step])

	return (
		<>

			{/* false header to display in local enviroment */}
			{!rumba.isProd() && (
				<div className='w-100 fixed-header df aic px-10' aria-hidden>
					<button 
						className='button black b-0 r3 text s15-600' 
						tabIndex={-1}
						onClick={() => {
							deleteItem('booking-data')
							window.location.reload();
						}}
					>
						Reset
					</button>
				</div>
			)}
			{/* pseudo router, allows router behavior in single page application */}
			{	
				//steps 2, 3, 4
				route === ROUTES.TABLE_LAYOUT ? 
				(<TableLayoutPage />) 
				:
				//step 5
				route === ROUTES.PERSONAL_DETAILS ? 
				(<PersonalDetailsPage />) 
				:
				//calendar widget
				route === ROUTES.CALENDAR ? 
				(<CalendarWidget />) 
				:
				//step 1
				//fallback to date selection page
				(<DateSelectionPage />)
			}
			{/* main app dialog, access by dialogHandles.get('app-dialog') */}
			<Dialog
				id="app-dialog"
				availableDialogs={[
					'add-children',
					'max-children',
					'product',
					'surcharge',
					'zone',
					'error',
					'loading',
					'unassigned-seats',
					'cam'
				]}
			/>
		</>
	)
}

export const App = () => (
	<AppProvider>
		<BookingEngine />
	</AppProvider>
)
